import { useMemo, useState } from "react";
import {
  LayoutGrid, LogOut, User as UserIcon, MoreHorizontal, FolderKanban, Store,
  PackageSearch, ClipboardList, ListChecks, ClipboardCheck, UserPlus,
  Undo2, BarChart3, Inbox, Wrench, Search, ChevronRight, DownloadCloud,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { ContextSwitcher } from "./ContextSwitcher";
import { RefreshButton } from "../common/RefreshButton";
import { hardRefresh } from "../../lib/hardRefresh";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";

// Bottom-bar item metadata (short labels + compact icons), resolved against permissions.
const ADMIN_PRIMARY = [
  { key: "dashboard", label: "Home", icon: LayoutGrid },
  { key: "material-register", label: "Material", icon: PackageSearch, perm: "MATERIAL.VIEW" },
  { key: "data-entry", label: "Data Entry", icon: ClipboardList, perm: "DATA_ENTRY.CREATE" },
  { key: "reports", label: "Reports", icon: BarChart3, perm: "REPORTS.REQUEST_EXPORT" },
];

const OPERATIONAL_PRIMARY = [
  { key: "dashboard", label: "Home", icon: LayoutGrid },
  { key: "grn", label: "GRN", icon: ClipboardCheck, perm: "MATERIAL.GRN" },
  { key: "allocation", label: "Allocate", icon: UserPlus, perm: "MATERIAL.ALLOCATE" },
  { key: "returns", label: "Returns", icon: Undo2, perm: "MATERIAL.RETURN_ACCEPT" },
  { key: "data-entry", label: "Data Entry", icon: ClipboardList, perm: "DATA_ENTRY.CREATE" },
  { key: "data-entry-register", label: "Register", icon: ListChecks, perm: "DATA_ENTRY.VIEW" },
  { key: "material-register", label: "Search", icon: Search, perm: "MATERIAL.VIEW" },
  { key: "reports", label: "Reports", icon: BarChart3, perm: "REPORTS.REQUEST_EXPORT" },
];

export function MobileShell({ children }) {
  const { user, logout, has } = useAuth();
  const { MODULES, active, openTab, openFieldTab, fieldTab, ctx, options } = useWorkspace();
  const [moreOpen, setMoreOpen] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(false);

  const visibleModules = MODULES.filter((m) => !m.perm || has(m.perm));
  const activeTitle = MODULES.find((m) => m.key === active)?.title || "Dashboard";

  // A field-restricted user only operates the Field Workspace; give them a task-focused bar.
  const isFieldOnly = has("FIELD_MATERIAL.VIEW") && !user?.is_admin
    && !has("MATERIAL.GRN") && !has("MATERIAL.ALLOCATE") && !has("DATA_ENTRY.CREATE");

  // Bottom-bar primary items — permission/capability driven, max 4 (+ More).
  const primary = useMemo(() => {
    if (isFieldOnly) {
      return [
        { key: "dashboard", label: "Home", icon: LayoutGrid, onClick: () => openTab("dashboard"), isActive: active === "dashboard" },
        { key: "assigned", label: "Assigned", icon: Inbox, onClick: () => openFieldTab("pending"), isActive: active === "field-workspace" && fieldTab === "pending" },
        { key: "install", label: "Install", icon: Wrench, onClick: () => openFieldTab("ack"), isActive: active === "field-workspace" && fieldTab === "ack" },
        { key: "field-returns", label: "Returns", icon: Undo2, onClick: () => openFieldTab("return"), isActive: active === "field-workspace" && fieldTab === "return" },
      ];
    }
    const src = user?.is_admin ? ADMIN_PRIMARY : OPERATIONAL_PRIMARY;
    return src
      .filter((it) => !it.perm || has(it.perm))
      .slice(0, 4)
      .map((it) => ({ ...it, onClick: () => openTab(it.key), isActive: active === it.key }));
  }, [isFieldOnly, user, active, fieldTab, has, openTab, openFieldTab]);

  const multiProject = (options.projects || []).length > 1;
  const projectLabel = ctx.project
    ? (ctx.project.project_code || ctx.project.project_name)
    : "All Projects";
  const stores = options.stores || [];
  const storeLabel = !options.is_admin && stores.length
    ? (stores.length === 1 ? (stores[0].store_name || stores[0].store_code) : `${stores.length} stores`)
    : null;

  const groups = visibleModules.reduce((acc, m) => {
    (acc[m.group] = acc[m.group] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Compact header: title + active-project chip */}
      <header className="fixed top-0 left-0 right-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-slate-200 bg-white px-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-[#7C3AED] font-display text-sm font-bold text-white">MF</div>
          <span className="truncate font-display text-sm font-semibold text-[#5B21B6]">{activeTitle}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {storeLabel && (
            <span data-testid="mobile-store-chip" title={stores.map((s) => s.store_name).join(", ")}
              className="flex max-w-[8rem] items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
              <Store className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{storeLabel}</span>
            </span>
          )}
          <button
            data-testid="mobile-project-chip"
            onClick={() => multiProject && setCtxOpen(true)}
            className={`flex max-w-[9rem] items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-medium text-[#5B21B6] ${multiProject ? "active:bg-teal-100" : "cursor-default"}`}
          >
            <FolderKanban className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{projectLabel}</span>
          </button>
          <RefreshButton />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="mobile-header-menu" className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-teal-100 font-display text-sm font-semibold text-teal-700">
                {(user?.full_name || "U").charAt(0)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="mobile-header-menu-content">
              <DropdownMenuLabel className="truncate">{user?.full_name || "User"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="mobile-header-hard-refresh" onSelect={() => hardRefresh()}>
                <DownloadCloud className="mr-2 h-4 w-4 text-teal-700" /> Get Latest Update
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="mobile-header-logout" onSelect={() => logout()} className="text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="px-4 pt-20">{children}</main>

      {/* Active-context sheet (opened from the project chip when multiple projects exist) */}
      <Sheet open={ctxOpen} onOpenChange={setCtxOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="mobile-context-sheet">
          <SheetHeader><SheetTitle className="font-display">Active Context</SheetTitle></SheetHeader>
          <div className="py-6"><ContextSwitcher /></div>
        </SheetContent>
      </Sheet>

      {/* Bottom navigation (role/permission-aware) */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex h-16 items-stretch justify-around border-t border-slate-200 bg-white px-1">
        {primary.map((it) => {
          const Icon = it.icon;
          return (
            <button
              key={it.key}
              data-testid={`mobile-nav-${it.key}`}
              onClick={it.onClick}
              className={`flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium ${it.isActive ? "text-[#6D28D9]" : "text-slate-400"}`}
            >
              <Icon className="h-5 w-5" />
              <span className="truncate">{it.label}</span>
            </button>
          );
        })}

        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-nav-more" className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-slate-400">
              <MoreHorizontal className="h-5 w-5" /> More
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl" data-testid="mobile-more-sheet">
            <SheetHeader><SheetTitle className="font-display">All Modules</SheetTitle></SheetHeader>

            {multiProject && (
              <button
                data-testid="mobile-more-context"
                onClick={() => { setMoreOpen(false); setCtxOpen(true); }}
                className="mt-4 flex w-full items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-3 py-2.5 text-sm font-medium text-[#5B21B6]"
              >
                <span className="flex items-center gap-2"><FolderKanban className="h-4 w-4" /> Switch Project · {projectLabel}</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            )}

            <div className="space-y-4 py-4">
              {Object.entries(groups).map(([group, mods]) => (
                <div key={group}>
                  <div className="px-1 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{group}</div>
                  <div className="grid grid-cols-3 gap-3">
                    {mods.map((m) => {
                      const Icon = m.icon;
                      return (
                        <button
                          key={m.key}
                          data-testid={`mobile-more-module-${m.key}`}
                          onClick={() => { openTab(m.key); setMoreOpen(false); }}
                          className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 p-3 text-center active:bg-slate-50"
                        >
                          <Icon className="h-6 w-6 text-[#6D28D9]" />
                          <span className="text-[11px] font-medium leading-tight text-slate-700">{m.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="border-t border-slate-100 pt-4">
                <div className="text-sm font-medium text-slate-800">{user?.full_name}</div>
                <div className="text-xs text-slate-500">{user?.email}</div>
                <div className="mt-0.5 text-xs text-slate-400">{user?.is_admin ? "Global Admin" : "Scoped user"}</div>
                <button data-testid="mobile-hard-refresh" onClick={hardRefresh} className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-teal-50 py-3 text-sm font-medium text-teal-700">
                  <DownloadCloud className="h-4 w-4" /> Get Latest Update
                </button>
                <p className="mt-1 text-center text-[10px] text-slate-400">Clears cache & reloads the newest version after a deployment.</p>
                <button data-testid="mobile-logout" onClick={logout} className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-red-50 py-3 text-sm font-medium text-red-600">
                  <LogOut className="h-4 w-4" /> Log out
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
